import React from "react";
import { getTeamInfo, TeamInfoOptionsType } from "./getTeamInfo";
import { normalizeSport } from "shared/src/utils/sportMap";
const defaultReturn = { league: undefined, colors: [undefined, undefined, undefined], abbreviation: undefined};

/**
 * Resolves team colors, league, abbreviation, and name from a search term.
 * Looks up the team in the teams map (via `getTeamInfo`) and extracts color metadata.
 *
 * For soccer/football and rugby teams, the third color slot contains the jersey type
 * (e.g., "stripeVertical") instead of a tertiary color.
 *
 * @param searchTerm - Team name, abbreviation, or alias to search for in the teams map.
 * @param options - Optional. `{ limitTo: string[] }` to restrict search to specific leagues/competitionTypes.
 *
 * @returns `{ league, name, abbreviation, colors }` where:
 *   - `league` — The team's league identifier (e.g., "nfl", "nba", "football")
 *   - `name` — The team's full resolved name
 *   - `abbreviation` — Formatted as `"league-abbr"` (e.g., `"nba-bos"`), used for logo resolution
 *   - `colors` — Array of `[primary, secondary, tertiary|jerseyType]` hex color values
 *
 * @example
 * ```tsx
 * const { colors, abbreviation, league } = useTeamColors("Boston Celtics");
 * // colors: ["#007A33", "#BA9653", "#963821"]
 * // abbreviation: "nba-bos"
 * // league: "nba"
 *
 * // Restrict to a specific league
 * const { colors } = useTeamColors("Real Madrid", { limitTo: ["football"] });
 * ```
 */
export const useTeamColors = (searchTerm: string, options?: TeamInfoOptionsType): TeamColorsReturnType => {
    if (!searchTerm) return defaultReturn;
    const foundTeam = getTeamInfo(searchTerm, options);
    if (foundTeam && "metadata" in foundTeam) {
        const { metadata: { colors, attributes } } = foundTeam;
        const _league = normalizeSport(foundTeam.league);
        const isJerseyType = _league === "football" || foundTeam.league === "rugby";
        const colorArr: (string)[] = colors ? [colors.primary?.value, colors.secondary?.value, isJerseyType ? attributes?.jerseyType ?? "" : colors.tertiary?.value ?? ""] : [];
        return { league: foundTeam.league, name: foundTeam.name, abbreviation: `${foundTeam.league}-${foundTeam.abbreviation.toLowerCase()}`, colors: colorArr }
    }
  return defaultReturn;
};
export type TeamColorsReturnType = { league?: string, abbreviation?: string, colors: string[] | undefined[], name?: string }